import React from "react";
import { Typography, Stack, Box } from "@mui/material";
import { Organisation } from "../../types";
import { AttendeeItem } from "./attendee-select";
import TeamChip from "../chips/team-chip";
import { UserAvatar } from "../avatars";
import { TeamSummary } from "../../types.new";

interface AttendeeOptionProps {
  liProps: React.HTMLAttributes<HTMLLIElement>;
  option: AttendeeItem;
  teamDictionary: Record<string, Organisation>;
  optionSelected: (item: AttendeeItem) => boolean;
}

const getDisplayedName = (
  fullName: string | undefined,
  email: string | undefined,
): string => {
  if (!fullName && !email) {
    return "Unknown user";
  }

  if (fullName && !email) {
    return fullName;
  }

  if (email && !fullName) {
    return email;
  }

  return `${fullName}  •  ${email}`;
};

const getTeamBadges = (
  option: AttendeeItem,
  teamDictionary: Record<string, Organisation>,
): TeamSummary[] => {
  if (option.type === "team") {
    const team = teamDictionary[option.id];
    if (!team) {
      return [];
    }

    return [
      {
        teamId: team.organisationId,
        name: team.name,
        color: team.color || "#1976d2",
      },
    ];
  }

  return (option.user?.organisationIds || [])
    .map((teamId) => teamDictionary[teamId])
    .filter((team): team is Organisation => !!team)
    .map((team) => ({
      teamId: team.organisationId,
      name: team.name,
      color: team.color || "#1976d2",
    }));
};

const AttendeeOption: React.FC<AttendeeOptionProps> = ({
  liProps,
  option,
  teamDictionary,
  optionSelected,
}) => {
  const { key, ...optionProps } =
    liProps as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };

  const selected = optionSelected(option);

  const displayName =
    option.type === "team"
      ? option.name
      : option.user?.fullName ||
        getDisplayedName(option.user?.fullName, option.user?.email);

  const secondaryText = option.type === "team" ? "" : option.user?.email || "";

  const teamBadges = getTeamBadges(option, teamDictionary);
  const isTeamOption = option.type === "team";

  return (
    <Box
      component="li"
      key={key}
      {...optionProps}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        py: 0.75,
        px: 1,
        minHeight: 52,
      }}
    >
      {isTeamOption ? (
        <TeamChip
          teamId={option.id}
          name={option.name}
          color={teamDictionary[option.id]?.color || "#1976d2"}
        />
      ) : (
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{ minWidth: 0, flex: 1 }}
        >
          <UserAvatar
            text={displayName}
            color="#64748b"
            sx={{
              width: 32,
              height: 32,
              fontSize: "0.72rem",
            }}
          />

          <Stack sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.96rem",
                fontWeight: 600,
                lineHeight: 1.2,
                color: selected ? "text.primary" : "text.primary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName}
            </Typography>
            {secondaryText ? (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.74rem",
                  color: "text.secondary",
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {secondaryText}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      )}

      {!isTeamOption && teamBadges.length > 0 ? (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ flexShrink: 0, ml: 1 }}
        >
          {teamBadges.map((team) => (
            <TeamChip
              key={`${option.id}-${team.teamId}`}
              teamId={team.teamId}
              name={team.name}
              color={team.color || "#1976d2"}
            />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
};

export default AttendeeOption;
